import React from "react";
import moment from "moment";
import {
    AndroidOutlined,
    AppleOutlined,
    CheckCircleOutlined,
    ChromeOutlined,
    CloseCircleOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined,
    StopOutlined,
} from "@ant-design/icons";

const handlePlaceholderTableLinkClick = (event) => event.preventDefault();

export const PlaceholderTableLink = ({ children }) => (
    <a href="/" onClick={ handlePlaceholderTableLinkClick }>{ children }</a>
);

export const getInitials = ({ name, surname }) => `${ name?.[ 0 ] || "" }${ surname?.[ 0 ] || "" }`;

export const hasScanLocation = (log) => (
    log?.latitude !== null &&
    log?.longitude !== null &&
    log?.latitude !== undefined &&
    log?.longitude !== undefined &&
    Number.isFinite(Number(log.latitude)) &&
    Number.isFinite(Number(log.longitude))
);

export const getScanPoints = (logs = []) => logs.filter(hasScanLocation);

export const getLocationLatitude = (location) => location?.latitude ?? location?.lat;
export const getLocationLongitude = (location) => location?.longitude ?? location?.lng;

export const hasLocationCoordinates = (location) => hasScanLocation({
    latitude: getLocationLatitude(location),
    longitude: getLocationLongitude(location),
});

export const formatWorkedSeconds = (value) => {
    const seconds = Number(value);

    if (!Number.isFinite(seconds))
    {
        return "-";
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    return `${ String(hours).padStart(2, "0") }:${ String(minutes).padStart(2, "0") }`;
};

export const getWorkDate = (value) => moment(value).format("YYYY-MM-DD");

const getWorkedSeconds = (shift) => {
    if (!shift.check_in_at || !shift.check_out_at)
    {
        return 0;
    }

    return Math.max(0, moment(shift.check_out_at).diff(moment(shift.check_in_at), "seconds"));
};

const sortByCreatedAt = (items = []) => (
    [ ...items ].sort((firstItem, secondItem) => (
        moment(firstItem.created_at).valueOf() - moment(secondItem.created_at).valueOf()
    ))
);

export const attachScanLogsToShifts = (shifts = [], scanLogs = []) => {
    const scanLogsByShiftId = scanLogs.reduce((logsByShift, log) => {
        if (!log.attendance_shift_id)
        {
            return logsByShift;
        }

        return {
            ...logsByShift,
            [ log.attendance_shift_id ]: [
                ...(logsByShift[ log.attendance_shift_id ] || []),
                log,
            ],
        };
    }, {});

    return shifts.map((shift) => ({
        ...shift,
        work_date: getWorkDate(shift.check_in_at),
        scan_logs: sortByCreatedAt(scanLogsByShiftId[ shift.id ]),
    }));
};

export const buildAttendanceDailySummaries = (shifts = []) => (
    Object.values(shifts.reduce((summaries, shift) => {
        const workDate = getWorkDate(shift.check_in_at);
        const key = `${ shift.ee_id }-${ shift.user_id }-${ workDate }`;
        const currentSummary = summaries[ key ];
        const checkInAt = !currentSummary || moment(shift.check_in_at).isBefore(currentSummary.check_in_at) ?
            shift.check_in_at :
            currentSummary.check_in_at;
        const checkOutAt = currentSummary?.has_open_shift || !shift.check_out_at ?
            null :
            (!currentSummary?.check_out_at || moment(shift.check_out_at).isAfter(currentSummary.check_out_at) ?
                shift.check_out_at :
                currentSummary.check_out_at
            );

        return {
            ...summaries,
            [ key ]: {
                id: key,
                user_id: shift.user_id,
                ee_id: shift.ee_id,
                worker: shift.worker,
                subcontractor: shift.subcontractor,
                work_date: workDate,
                check_in_at: checkInAt,
                check_out_at: checkOutAt,
                worked_seconds: (currentSummary?.worked_seconds || 0) + getWorkedSeconds(shift),
                has_open_shift: Boolean(currentSummary?.has_open_shift || !shift.check_out_at),
                scan_logs: sortByCreatedAt([
                    ...(currentSummary?.scan_logs || []),
                    ...(shift.scan_logs || []),
                ]),
            },
        };
    }, {})).sort((firstSummary, secondSummary) => (
        moment(secondSummary.work_date).valueOf() - moment(firstSummary.work_date).valueOf()
    ))
);

const scanReasonMap = {
    within_radius: {
        label: "CHECK_LOGS.Reason_WithinRadius",
        tone: "neutral",
        icon: null,
    },
    outside_radius: {
        label: "CHECK_LOGS.Reason_OutsideRadius",
        tone: "error",
        icon: <CloseCircleOutlined />,
    },
    fake_gps_suspected: {
        label: "CHECK_LOGS.Reason_FakeGpsSuspected",
        tone: "error",
        icon: <StopOutlined />,
    },
    gps_unavailable: {
        label: "CHECK_LOGS.Reason_GpsUnavailable",
        tone: "error",
        icon: <CloseCircleOutlined />,
    },
    permission_denied: {
        label: "CHECK_LOGS.Reason_PermissionDenied",
        tone: "error",
        icon: <StopOutlined />,
    },
    qr_invalid: {
        label: "CHECK_LOGS.Reason_QrInvalid",
        tone: "error",
        icon: <CloseCircleOutlined />,
    },
    duplicate_scan: {
        label: "CHECK_LOGS.Reason_DuplicateScan",
        tone: "warning",
        icon: <ExclamationCircleOutlined />,
    },
    unknown: {
        label: undefined,
        tone: "neutral",
        icon: <InfoCircleOutlined />,
    },
};

const scanResultMap = {
    accepted: {
        label: "CHECK_LOGS.Result_Accepted",
        tone: "success",
        icon: <CheckCircleOutlined />,
    },
    rejected: {
        label: "CHECK_LOGS.Result_Rejected",
        tone: "error",
        icon: <CloseCircleOutlined />,
    },
    ignored: {
        label: "CHECK_LOGS.Result_Ignored",
        tone: "warning",
        icon: <ExclamationCircleOutlined />,
    },
    unknown: {
        label: "CHECK_LOGS.Result_Unknown",
        tone: "neutral",
        icon: <InfoCircleOutlined />,
    },
};

const ignoredScanReasons = [ "duplicate_scan" ];
const rejectedScanReasons = [
    "outside_radius",
    "fake_gps_suspected",
    "gps_unavailable",
    "permission_denied",
    "qr_invalid",
];

const platformMap = {
    android: { icon: <AndroidOutlined />, label: "Android" },
    ios: { icon: <AppleOutlined />, label: "iOS" },
    web: { icon: <ChromeOutlined />, label: "Web" },
};

export const getScanReasonMeta = (reason) => scanReasonMap[ reason ] || scanReasonMap.unknown;
export const getScanReasonLabel = (reason) => getScanReasonMeta(reason).label;
export const getScanResultMeta = (result, reason) => {
    if (scanResultMap[ result ])
    {
        return scanResultMap[ result ];
    }

    if (ignoredScanReasons.includes(reason))
    {
        return scanResultMap.ignored;
    }

    if (rejectedScanReasons.includes(reason))
    {
        return scanResultMap.rejected;
    }

    return reason ? scanResultMap.accepted : scanResultMap.unknown;
};
export const getPlatformMeta = (platform) => platformMap[ platform ] || platformMap.web;
